import { ChipTypes } from "helpers/getDefectChipTypesCc";

// Builds a [{ label, value }] list summarising the user's currently applied
// filters. Source of truth is FilterContext.filterValues — each entry has a
// .value (the selected option) and .params (the URL/API representation).
//
// We deliberately avoid hardcoding which filter keys exist; instead we walk
// what's present and rely on a small label + reader registry for the keys we
// know about. Unknown filter keys are skipped rather than displayed raw, since
// raw param shapes wouldn't be useful to a user.

const SHOW_ALL = "Show All";

// Chips render on the page as `${ChipTypes(chipType)[0].caption} : ${value}`
// (see EMIChip). Match that shape here so the modal's "current page filters"
// list reads identically to what the user sees in the chip row above.
const chipLabel = (chip) =>
	ChipTypes(chip?.chipType)?.[0]?.caption || "Filter";

const chipDisplayValue = (chip) => {
	if (!chip) return "";
	// chipType === 8 is the boolean / yes-no chip variant; everything else uses value.
	if (chip.chipType === 8) return chip.value ? "Yes" : "No";
	return chip.value ?? chip.name ?? "";
};

const valueName = (value) => {
	if (!value) return null;
	if (Array.isArray(value)) {
		const names = value.map((v) => v?.name).filter(Boolean);
		return names.length ? names.join(", ") : null;
	}
	return value?.name || null;
};

const filterReaders = {
	defectStatus: { label: "Status", read: (entry) => valueName(entry.value) || SHOW_ALL },
	serviceStatus: { label: "Status", read: (entry) => valueName(entry.value) || SHOW_ALL },
	timeframe: { label: "Timeframe", read: (entry) => valueName(entry.value) || SHOW_ALL },
	department: { label: "Department", read: (entry) => valueName(entry.value) || SHOW_ALL },
	model: { label: "Model", read: (entry) => valueName(entry.value) || SHOW_ALL },
	role: { label: "Role", read: (entry) => valueName(entry.value) || SHOW_ALL },
	asset: { label: "Asset", read: (entry) => valueName(entry.value) || SHOW_ALL },
	interval: { label: "Interval", read: (entry) => valueName(entry.value) || SHOW_ALL },
	riskRatings: { label: "Risk Rating", read: (entry) => valueName(entry.value) || SHOW_ALL },
	defectType: { label: "Type", read: (entry) => valueName(entry.value) || SHOW_ALL },
};

// Chips need bespoke handling because each chip carries its own label/value
// pair (the registry above maps one filter key -> one summary row, but a
// chip array produces N rows, one per chip).
const readChips = (entry) => {
	const chips = entry?.value;
	if (!Array.isArray(chips) || chips.length === 0) return [];
	return chips
		.map((chip) => ({
			label: chipLabel(chip),
			value: chipDisplayValue(chip),
		}))
		.filter((row) => row.value);
};

export const formatFilterSummary = (filterValues = {}) => {
	// Chips first — they're typically the most specific selection the user
	// has made (a particular Model, Asset, Risk Rating) so they should rank
	// above broader dropdowns like Timeframe / Status when downstream
	// consumers (e.g. suggestSubscriptionName) take the leading entries.
	const summary = readChips(filterValues.chips);
	for (const [key, entry] of Object.entries(filterValues)) {
		if (key === "chips") continue;
		const reader = filterReaders[key];
		if (!reader || !entry) continue;
		const value = reader.read(entry);
		if (!value) continue;
		summary.push({ label: reader.label, value });
	}
	return summary;
};

export const formatFilterSummaryInline = (filterValues = {}) =>
	formatFilterSummary(filterValues)
		.map(({ label, value }) => `${label} = ${value}`)
		.join(", ");
